import React from 'react'
import {last} from 'lodash'
import {createRoot} from 'react-dom/client'
import {Link, useLocation} from 'wouter'
import {Loading} from './components'
import App from './components/App'
import {CacheProvider} from '@data-client/react'
import {LightGallery} from 'lightgallery/lightgallery'
import {CssBaseline, LinkProps} from '@mui/material'
import {createTheme, responsiveFontSizes} from '@mui/material/styles'
import {ThemeProvider} from '@mui/material'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import './css/main.css'

// a global namespace to handle some tricky things:
// - a way to detect if there is a new version of the web app
// - last few errors so they can be reported to the server
// - work around light gallery issues
//
declare global {
  interface Window {
    app: {
      errors: {ts: string; msg: string}[]
      upgrade: boolean
      lightbox?: LightGallery
    }
    consoleError: (...args: string[]) => void
  }
}
if (!window.app) {
  window.app = {
    errors: [],
    upgrade: false,
  }
}

// record the last 10 error messages for debug reporting
//
window.consoleError = console.error
console.error = (...args) => {
  window.app.errors.push({
    ts: new Date().toISOString(),
    msg: args.join(' '),
  })
  if (window.app.errors.length > 10) {
    window.app.errors.shift()
  }
  window.consoleError(...args)
}

async function checkForUpdate() {
  // a different checksum of /index.html signals that a new version is available
  try {
    let version = localStorage.getItem('version')
    const r = await fetch('/', {method: 'HEAD'})
    const chksum = r.headers.get('etag') || '???'
    if (!version) {
      localStorage.setItem('version', chksum)
      version = chksum
    }
    if (version !== chksum) {
      window.app.upgrade = true
      localStorage.setItem('version', chksum)
    }
  } catch (err) {
    console.error('failed to check for new version:', err)
  }
  if (!window.app.upgrade) {
    setTimeout(checkForUpdate, 10 * 60 * 1000)
  }
}
checkForUpdate()

function reloadOnNewVersion(evt: React.SyntheticEvent, to: string) {
  if (window.app.upgrade) {
    evt.preventDefault()
    window.location.assign(to)
  }
}

function normalize(abs: string, rel: string) {
  const dirs = abs.split('/')
  while (rel.startsWith('../')) {
    rel = rel.slice(3)
    dirs.pop()
  }
  return dirs.join('/') + '/' + rel
}

// need the following to handle relative paths and external links
interface LinkBehaviorProps {
  href: string
  children: React.ReactNode
}
const LinkBehavior = React.forwardRef<HTMLAnchorElement, LinkBehaviorProps>(({href, ...props}, ref) => {
  const [location] = useLocation()
  // TODO: full page load if scope in query string
  if (href.startsWith('./')) {
    // eslint-disable-next-line no-param-reassign
    href = location + href.slice(last(location) === '/' ? 2 : 1)
  } else if (href.startsWith('../')) {
    // eslint-disable-next-line no-param-reassign
    href = normalize(location, href)
  } else if (!href.startsWith('/')) {
    return (
      <a {...props} ref={ref} href={href} target="_blank" rel="nofollow noopener noreferrer">
        {props.children}
      </a>
    )
  }
  return (
    <Link href={href} onClick={e => reloadOnNewVersion(e, href)} {...props}>
      {props.children}
    </Link>
  )
})

const theme = responsiveFontSizes(
  createTheme({
    components: {
      MuiLink: {
        defaultProps: {
          component: LinkBehavior,
        } as LinkProps,
      },
      MuiButtonBase: {
        defaultProps: {
          LinkComponent: LinkBehavior,
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            zIndex: 'auto',
          },
          extended: {
            borderRadius: 5,
          },
        },
      },
      MuiSpeedDial: {
        styleOverrides: {
          actions: {
            padding: 10,
            backgroundColor: '#eee',
            border: '1px solid #ccc',
            borderRadius: 5,
          },
          actionsClosed: {
            backgroundColor: 'inherit',
            border: 'none',
          },
        },
      },
      MuiSpeedDialAction: {
        styleOverrides: {
          staticTooltipLabel: {
            backgroundColor: '#eee',
            borderTop: '1px solid #ccc',
            borderLeft: '1px solid #ccc',
            borderBottom: '1px solid #ccc',
            borderRight: 0,
            borderRadius: 5,
            boxShadow: 'none',
          },
        },
      },
      // MuiTypography: {
      //   styleOverrides: {
      //     h1: {
      //       color: '#666',
      //     },
      //     h2: {
      //       color: '#666',
      //     },
      //     h3: {
      //       color: '#666',
      //     },
      //     h4: {
      //       color: '#666',
      //     },
      //     h5: {
      //       color: '#666',
      //     },
      //     h6: {
      //       color: '#666',
      //     },
      //     body1: {
      //       color: '#666',
      //     },
      //   },
      // },
    },
  })
)

// theme.typography.htmlFontSize = 16 * .625
// theme.typography.fontSize = 16
theme.palette.primary.main = '#137f9b'
theme.palette.secondary.main = '#fdb336'
theme.palette.common.black = '#333'
theme.palette.common.white = '#eee'
theme.typography.h1 = {
  color: theme.palette.secondary.main,
}
theme.typography.h2 = {
  color: theme.palette.secondary.main,
}
theme.typography.h3 = {
  color: theme.palette.secondary.main,
}
theme.typography.h4 = {
  color: theme.palette.secondary.main,
}
theme.typography.h5 = {
  color: theme.palette.secondary.main,
}

const root = createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <CssBaseline enableColorScheme />
    <CacheProvider>
      <React.Suspense fallback={<Loading />}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <App />
          </LocalizationProvider>
        </ThemeProvider>
      </React.Suspense>
    </CacheProvider>
  </React.StrictMode>
)
